import { useCallback, useState } from 'react';
import {
    Option,
    Select,
    Label,
    styled,
    Box,
    Row,
    Tooltip,
    AppText,
    ToastTypes,
} from '@streem/ui-react';
import { recordElementClicked } from '@streem/analytics';
import appLogger from '../../util/logging/app_logger';
import { useAudioInputPreferredDevices } from '../../hooks/use_audio_input_preferred_devices';
import {
    AudioInputOption,
    useAudioLevelPolling,
    useSetUpMediaStreamAndDevices,
} from '../../hooks/use_audio_input_select';
import { useGetAudioLevelGradient } from './media_settings_utils/use_get_audio_level_gradient';
import { useGlobalStore } from '../../hooks/use_global_context';
import { setPreferredAudioInput } from '@streem/toolbox';

const log = appLogger.extend('AudioInputSelect');

interface Props {
    open: boolean;
    onPermissionDenied: () => void;
    onPermissionAllowed: () => void;
}

const inputId = 'audio-input-select';
const audioInputDropdownOpenedId = 'ms-audio-input-select-opened';
const audioInputDropdownId = 'ms-audio-input-item-selected';

const AudioInputSelect = ({ open, onPermissionDenied, onPermissionAllowed }: Props) => {
    const { uiStore } = useGlobalStore();
    const [audioInputDevices, setAudioInputDevices] = useState<AudioInputOption[]>([]);
    const [placeholder, setPlaceholder] = useState<string>('Microphone selection unavailable');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { selectedAudioInputDevice, initialized, setSelectedAudioInputDevice } =
        useAudioInputPreferredDevices({
            audioInputDevices,
        });

    const onMenuOpen = useCallback(() => {
        setIsMenuOpen(true);
        recordElementClicked(audioInputDropdownOpenedId);
    }, []);

    const onMenuClose = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    const handleSelect = useCallback(
        (option: Option<string>) => {
            log.info('setting preferred audio input device', option);
            setSelectedAudioInputDevice(option);
            const previousSelectedAudioInputDevice = selectedAudioInputDevice;
            setPreferredAudioInput(option.value, option.label);
            recordElementClicked(audioInputDropdownId);

            if (previousSelectedAudioInputDevice) {
                uiStore.removeToast(previousSelectedAudioInputDevice?.label);
            }

            uiStore.addToast({
                content: `Microphone changed to ${option.label}`,
                flavor: ToastTypes.INFO,
                id: option.label,
                timeout: 3000,
            });
        },
        [setSelectedAudioInputDevice, selectedAudioInputDevice, uiStore],
    );

    return (
        <Wrapper>
            <Label semibold size="medium" htmlFor={inputId}>
                Select Microphone
            </Label>
            <Select
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                options={audioInputDevices}
                value={selectedAudioInputDevice}
                menuPlacement="bottom"
                onSelect={handleSelect}
                isDisabled={!initialized}
                id="audio-input-select"
                border={true}
                placeholder={placeholder}
                styles={{
                    menuList: () => ({
                        // prevent the menu list from overflowing the bottom of the wrapper
                        overflowY: 'scroll',
                        maxHeight: '9rem',
                    }),
                    container: base => ({
                        width: '18.125rem',
                        ...base,
                    }),
                    placeholder: base => ({
                        ...base,
                        textWrap: 'nowrap',
                    }),
                }}
                inputId={inputId}
            />
            <InputLevel
                setAudioInputDevices={setAudioInputDevices}
                setPlaceholder={setPlaceholder}
                onPermissionAllowed={onPermissionAllowed}
                onPermissionDenied={onPermissionDenied}
                open={open}
                isMenuOpen={isMenuOpen}
                initialized={initialized}
            />
        </Wrapper>
    );
};

const TooltipMessage = () => {
    return (
        <AppText size="small">Speak into the mic. If no input level, try different mic.</AppText>
    );
};

interface InputLevelProps extends Props {
    setAudioInputDevices: (props: AudioInputOption[]) => void;
    setPlaceholder: (txt: string) => void;
    isMenuOpen: boolean;
    initialized: boolean;
}
const InputLevel = (props: InputLevelProps) => {
    const {
        setAudioInputDevices,
        setPlaceholder,
        onPermissionDenied,
        onPermissionAllowed,
        open,
        isMenuOpen,
    } = props;

    const mediaStream = useSetUpMediaStreamAndDevices({
        setAudioInputDevices,
        onPermissionAllowed,
        onPermissionDenied,
        setPlaceholder,
        open,
    });

    const { audioLevel, enabledYn } = useAudioLevelPolling(mediaStream);

    const showTooltipMessaga = !isMenuOpen && audioLevel === 0 && enabledYn;

    const audioLevelGradients = useGetAudioLevelGradient(audioLevel);

    return (
        <Box mt="4">
            <AppText semibold>Input Level</AppText>

            <div>
                <AudioLevelWrapper data-testid="audio-level-container">
                    {audioLevelGradients.map((color, index) => {
                        if (index === 1) {
                            return (
                                <Tooltip
                                    key={index}
                                    message={<TooltipMessage />}
                                    showTooltip={showTooltipMessaga}
                                    placement="bottom-start"
                                    offsetValue={-16}
                                >
                                    <Indicator key={index} color={color} />
                                </Tooltip>
                            );
                        }
                        return <Indicator key={index} color={color} />;
                    })}
                </AudioLevelWrapper>
            </div>
        </Box>
    );
};

const Wrapper = styled.span({
    display: 'inline-block',
});

const AudioLevelWrapper = styled(Row)({
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginTop: '.5rem',
});

const Indicator = styled('div')<{ color: string }>(({ color }) => ({
    backgroundColor: color,
    height: '2.125rem',
    width: '0.75rem',
    transition: 'height 0.3s ease, background-color 0.3s ease',
}));

export default AudioInputSelect;
